import React, { useState, useEffect } from 'react';
import { Bar, BarChart, Tooltip, YAxis, ResponsiveContainer, CartesianGrid, XAxis } from 'recharts';

import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Modal,
  Slider,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import EditButton from '../../../../../Components/Buttons/EditButton';
import { reportPageStyles as styles } from '../styles';
import { KpiPercentileType, ReportTypes } from '../../../propTypes';
import {
  COLOR_GREY,
  COLOR_TOOLTIP_BACKGROUND,
  DARK_GREY,
  exchangeRates,
  investmentCalInputKeys,
  managementCalInputKeys,
  rentalArbitrageCalculatorInputFields,
} from 'src/const';
import { abbreviateNumber, calculateMonthlyMortgage, formatCurrency } from 'src/utils/functions';
import TooltipNew from '../../../../../HOC/Tooltips/TooltipNew';
import { infoIconStyle } from 'src/App/Dashboards/Market/styles/style';
import { COLOR_PRIMARY_1 } from 'src/colors';

const InputTextField = ({
  field,
  stateObj,
  handleValueChange,
  currency,
  disabled,
}: {
  field: {
    key: string;
    unit: string;
    label: string;
  };
  stateObj: any;
  handleValueChange: (key: string, event: any) => void;
  currency: string;
  disabled?: boolean;
}) => {
  return (
    <TextField
      id={field.label}
      label={field.label}
      variant="outlined"
      size="small"
      fullWidth
      defaultValue={stateObj[field.key]}
      value={
        stateObj[field.key] === ''
          ? ''
          : field.unit === '%'
          ? stateObj[field.key].toString()
          : formatCurrency(stateObj[field.key].toString())
      }
      onChange={event => {
        handleValueChange(field.key, event);
      }}
      type={field.unit === '%' ? 'number' : 'tel'}
      error={!stateObj[field.key]}
      disabled={disabled ? disabled : false}
      InputProps={
        field.unit === '$'
          ? {
              startAdornment: (
                <InputAdornment position="start">{exchangeRates[currency].symbol}</InputAdornment>
              ),
            }
          : {
              endAdornment: <InputAdornment position="end">{field.unit}</InputAdornment>,
            }
      }
    />
  );
};

const ImmutableDataField = (field, value, currency) => {
  return (
    <div>
      <Typography sx={{ fontSize: 16, color: COLOR_GREY }}>{field.label}</Typography>
      <Typography
        sx={{
          fontSize: 18,
          color: 'black',
          fontWeight: 500,
        }}
      >
        {field.unit && exchangeRates[currency].symbol}
        {field.unit == '$' ? formatCurrency(value.toString()) : value}
      </Typography>
    </div>
  );
};

const BuyCalculator = ({
  currency,
  reportCalculatorInputs,
  revenueProjection,
  editOpen,
  setEditOpen,
  reportId,
  reportType,
  handleReport,
  trackEditPerformed,
  reportKPIData,
}: {
  currency: string;
  reportCalculatorInputs: any;
  revenueProjection: number;
  editOpen: boolean;
  setEditOpen: React.Dispatch<React.SetStateAction<boolean>>;
  reportId: string;
  reportType: ReportTypes;
  handleReport: any;
  trackEditPerformed: (editType: any) => void;
  reportKPIData: any;
}) => {
  const theme = useTheme();

  const {
    loan: { is_loan_taken, loan_tenure, down_payment, interest_rate },
    annual_operating_expenses: {
      management_fee,
      property_tax,
      lodging_tax,
      hoa_fee_annual,
      maintenance_annual,
      insurance_annual,
      electricity_monthly,
      wifi_monthly,
      gas_monthly,
      others_annual,
      mortgage_monthly,
    },
    startup_costs: { furnishing_cost, closing_cost },
    property_price,
    annual_operating_expenses_total,
    property_appreciation_rate,
    startup_costs_total,
  } = reportCalculatorInputs;

  const immutableCalculatorData = {
    property_price,
    annual_revenue_ltm: revenueProjection,
    annual_operating_expenses_total: reportKPIData.yearly_expense,
    startup_costs_total,
  };

  const [mutableCalculatorInputs, setMutableCalculatorInputs] = useState({
    property_price,
    management_fee,
    property_tax,
    lodging_tax,
    hoa_fee_annual,
    maintenance_annual,
    insurance_annual,
    electricity_monthly,
    wifi_monthly,
    gas_monthly,
    others_annual,
    furnishing_cost,
    closing_cost,
    is_loan_taken: is_loan_taken == 1 ? true : false,
    loan_tenure,
    down_payment,
    interest_rate,
    mortgage_monthly,
    property_appreciation_rate,
  });

  const [loanTimePeriods, setLoanTimePeriods] = useState([
    { label: '10 Years', year: 10 },
    { label: '15 Years', year: 15 },
    { label: '30 Years', year: 30 },
  ]);
  const [downPaymentPercentage, setDownPaymentPercentage] = useState(0);

  useEffect(() => {
    if (mutableCalculatorInputs.property_price > 0) {
      const calculatedDownPaymentPcnt = parseFloat(
        (
          (mutableCalculatorInputs.down_payment / mutableCalculatorInputs.property_price) *
          100
        ).toFixed(2)
      );
      setDownPaymentPercentage(calculatedDownPaymentPcnt);
    }
  }, [mutableCalculatorInputs.down_payment, mutableCalculatorInputs.property_price]);

  const handleLoanTaken = value => {
    setMutableCalculatorInputs({
      ...mutableCalculatorInputs,
      is_loan_taken: value.target.checked,
    });
  };

  const handleOnTimePeriodChange = (event, value) => {
    const customYear = value ? parseInt(value.match(/\d+/)?.[0]) : null;
    if (customYear && customYear > 0) {
      // Check if the entered year already exists in loanTimePeriods
      const yearExists = loanTimePeriods.some(period => period.year == customYear);
      if (!yearExists) {
        // If it doesn't exist, add it to the loanTimePeriods array
        const newPeriods = [...loanTimePeriods, { label: `${customYear} Years`, year: customYear }];
        // Sort newPeriods based on years
        newPeriods.sort((a, b) => a.year - b.year);
        setLoanTimePeriods(newPeriods);
      }
    }
  };

  const handleLoanPeriodChange = (event, value) => {
    if (value) {
      setMutableCalculatorInputs({
        ...mutableCalculatorInputs,
        loan_tenure: value.year,
      });
    }
  };

  useEffect(() => {
    const mortgageAmount = calculateMonthlyMortgage(
      mutableCalculatorInputs.property_price,
      mutableCalculatorInputs.down_payment,
      mutableCalculatorInputs.interest_rate,
      mutableCalculatorInputs.loan_tenure
    );
    setMutableCalculatorInputs({
      ...mutableCalculatorInputs,
      mortgage_monthly: mortgageAmount > 0 || !isNaN(mortgageAmount) ? mortgageAmount : 0,
    });
  }, [
    mutableCalculatorInputs.property_price,
    mutableCalculatorInputs.down_payment,
    mutableCalculatorInputs.interest_rate,
    mutableCalculatorInputs.loan_tenure,
  ]);

  const handleValueChange = (key: string, event: any) => {
    let value = event.target.value;
    if (!value) {
      value = '0';
    }

    if (key !== 'interest_rate') {
      value = parseFloat(value.replaceAll(',', ''));
    } else {
      value = Number(parseFloat(value.replaceAll(',', '')).toFixed(2));
    }

    if (key === 'down_payment') {
      setMutableCalculatorInputs({
        ...mutableCalculatorInputs,
        [key]: value > 0 ? value : 0,
      });
      let percentCalculated = (value / mutableCalculatorInputs.property_price) * 100;
      if (percentCalculated < 0 || isNaN(percentCalculated)) {
        percentCalculated = 0;
      }
      setDownPaymentPercentage(parseFloat(percentCalculated.toFixed(1)));
    }

    if (key === 'interest_rate') {
      let tempInterestRate = value;
      if (tempInterestRate <= 0 || isNaN(tempInterestRate)) tempInterestRate = 0;
      setMutableCalculatorInputs({
        ...mutableCalculatorInputs,
        [key]: tempInterestRate,
      });
    }

    if (key === 'downPaymentPercentage') {
      value < 0 || isNaN(value)
        ? setDownPaymentPercentage(0)
        : setDownPaymentPercentage(parseFloat(value.toFixed(1)));

      let downPaymentValue =
        (mutableCalculatorInputs.property_price / 100) * parseFloat(value.toFixed(1));

      mutableCalculatorInputs.down_payment = downPaymentValue;
    }

    if (
      (key === 'management_fee' || key === 'lodging_tax' || key === 'property_tax') &&
      (value > 100 || value < 0)
    ) {
      window.alert('Please enter a number between 0 and 100');
      setMutableCalculatorInputs({
        ...mutableCalculatorInputs,
        [key]: mutableCalculatorInputs[key],
      });
    } else if (value < 0) {
      window.alert('Please enter a number greater than 0');
      setMutableCalculatorInputs({
        ...mutableCalculatorInputs,
        [key]: mutableCalculatorInputs[key],
      });
    } else {
      setMutableCalculatorInputs({
        ...mutableCalculatorInputs,
        [key]: value,
      });
    }
  };

  const handlePropertyAppreciationRateChange = (event: any, newValue: number | number[]) => {
    setMutableCalculatorInputs({
      ...mutableCalculatorInputs,
      property_appreciation_rate: newValue,
    });
  };

  const handleOnBuyCalcApply = () => {
    trackEditPerformed('calculator');
    const buyCalculatorMutableFieldsJsonObj = {
      update_type: 'calculator',
      id: reportId,
      report_type: reportType,
      ...mutableCalculatorInputs,
      is_loan_taken: mutableCalculatorInputs.is_loan_taken ? 1 : 0,
    };
    handleReport('UPDATE', buyCalculatorMutableFieldsJsonObj);
  };

  const BuyCalculatorEditModal = () => {
    return (
      <Modal open={editOpen} onClose={() => setEditOpen(false)}>
        <Box sx={styles.filterModalBodyRoot}>
          <IconButton
            onClick={() => {
              setEditOpen(false);
            }}
            sx={styles.filterModalCloseIcon}
          >
            <CloseIcon />
          </IconButton>

          <Grid container xs justifyContent="space-between" sx={{ mt: '10px' }}>
            {/* Property price section */}
            <Grid
              item
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
            >
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontSize: '16px' }}>
                  Property Price
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <InputTextField
                  field={{
                    key: 'property_price',
                    label: 'Property Price',
                    unit: '$',
                  }}
                  stateObj={mutableCalculatorInputs}
                  handleValueChange={handleValueChange}
                  currency={currency}
                />
              </Grid>
            </Grid>

            {/* Loan Section */}
            <Grid
              item
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
              sx={{ mt: 2 }}
            >
              <Grid container item xs={12} alignItems="center" justifyContent="flex-start">
                <Grid item>
                  <Typography variant="h6" sx={{ fontSize: '16px' }}>
                    Are you taking a loan?
                  </Typography>
                </Grid>
                <Grid item>
                  <Switch
                    checked={mutableCalculatorInputs['is_loan_taken']}
                    onChange={handleLoanTaken}
                  />
                </Grid>
              </Grid>

              <Grid item xs={6}>
                <Autocomplete
                  disablePortal
                  id="combo-box-demo"
                  options={loanTimePeriods}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '40px !important',
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '0px !important',
                    },
                  }}
                  disabled={!mutableCalculatorInputs['is_loan_taken']}
                  renderInput={params => <TextField {...params} label="Years" />}
                  defaultValue={{ label: '30 Years', year: 30 }}
                  onInputChange={handleOnTimePeriodChange}
                  onChange={(event, value) => {
                    handleLoanPeriodChange(event, value);
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <InputTextField
                  field={{
                    key: 'down_payment',
                    label: 'Down Payment',
                    unit: '$',
                  }}
                  stateObj={mutableCalculatorInputs}
                  handleValueChange={handleValueChange}
                  currency={currency}
                  disabled={!mutableCalculatorInputs['is_loan_taken']}
                />
              </Grid>

              <Grid item xs={6}>
                <InputTextField
                  field={{
                    key: 'interest_rate',
                    label: 'Interest Rate',
                    unit: '%',
                  }}
                  stateObj={mutableCalculatorInputs}
                  handleValueChange={handleValueChange}
                  currency={currency}
                  disabled={!mutableCalculatorInputs['is_loan_taken']}
                />
              </Grid>

              <Grid item xs={6}>
                <Typography sx={{ fontSize: 13, color: COLOR_GREY }}>
                  Estimated Monthly Mortgage
                </Typography>
                <Typography sx={{ fontSize: 17 }}>
                  {exchangeRates[currency].symbol}
                  {formatCurrency(
                    (mutableCalculatorInputs.is_loan_taken
                      ? mutableCalculatorInputs.mortgage_monthly
                      : 0
                    ).toString()
                  )}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    fontStyle: 'italic',
                  }}
                >
                  <InfoOutlinedIcon sx={{ ...infoIconStyle, color: '#000' }} /> Your down payment of{' '}
                  {exchangeRates[currency].symbol +
                    formatCurrency(mutableCalculatorInputs['down_payment'].toString())}{' '}
                  is {downPaymentPercentage}% of{' '}
                  {exchangeRates[currency].symbol + formatCurrency(property_price.toString())}
                </Typography>
              </Grid>
            </Grid>

            {/* Operating expenses setion */}
            <Grid
              item
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
              sx={{ mt: 2 }}
            >
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontSize: '16px' }}>
                  Operating Expenses
                </Typography>
              </Grid>

              <Grid item container spacing={2}>
                {investmentCalInputKeys.annualExpenses.subfields &&
                  investmentCalInputKeys.annualExpenses.subfields.map((subfield, subfieldIndex) => {
                    return (
                      <Grid item xs={6} key={subfieldIndex}>
                        <InputTextField
                          field={subfield}
                          stateObj={mutableCalculatorInputs}
                          handleValueChange={handleValueChange}
                          currency={currency}
                        />
                      </Grid>
                    );
                  })}
              </Grid>
            </Grid>

            {/* Startup costs section */}
            <Grid
              item
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
              sx={{ mt: 2 }}
            >
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontSize: '16px' }}>
                  Startup Costs
                </Typography>
              </Grid>

              <Grid item container spacing={2}>
                {investmentCalInputKeys.startupCosts.subfields &&
                  investmentCalInputKeys.startupCosts.subfields.map((subfield, subfieldIndex) => (
                    <Grid item xs={6} key={subfieldIndex}>
                      <InputTextField
                        field={subfield}
                        stateObj={mutableCalculatorInputs}
                        handleValueChange={handleValueChange}
                        currency={currency}
                      />
                    </Grid>
                  ))}
              </Grid>
            </Grid>

            <Grid
              item
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
              sx={{ mt: 2 }}
            >
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontSize: '16px' }}>
                  Property Appreciation
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Slider
                  size="small"
                  value={mutableCalculatorInputs.property_appreciation_rate}
                  min={0}
                  max={15}
                  step={1}
                  marks={[
                    {
                      value: 0,
                      label: '0%',
                    },
                    {
                      value: 15,
                      label: '15%',
                    },
                  ]}
                  draggable={true}
                  onChange={handlePropertyAppreciationRateChange}
                  valueLabelDisplay="auto"
                  valueLabelFormat={x => `${x}%`}
                  sx={{
                    color: COLOR_PRIMARY_1,
                    width: '100%',
                  }}
                />
              </Grid>
            </Grid>

            <Grid container item xs justifyContent="flex-end" sx={{ mt: '20px' }}>
              <Button onClick={handleOnBuyCalcApply} sx={styles.actionSectionButton}>
                Apply
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    );
  };

  return (
    <>
      <Grid
        item
        container
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        columnSpacing={4}
        rowSpacing={4}
      >
        {Object.keys(investmentCalInputKeys).map((field: string) => {
          return (
            <Grid
              item
              key={field}
              xs={6}
              sx={{
                [theme.breakpoints.down('md')]: {
                  marginRight: 10,
                },
              }}
            >
              <TooltipNew
                title={investmentCalInputKeys[field].label}
                content={investmentCalInputKeys[field].tooltip}
                arrow
              >
                {ImmutableDataField(
                  investmentCalInputKeys[field],
                  immutableCalculatorData[investmentCalInputKeys[field].key],
                  currency
                )}
              </TooltipNew>
            </Grid>
          );
        })}
      </Grid>
      {BuyCalculatorEditModal()}
    </>
  );
};

const RentCalculator = ({
  currency,
  reportCalculatorInputs,
  revenueProjection,
  editOpen,
  setEditOpen,
  reportId,
  reportType,
  handleReport,
  reportKPIData,
  trackEditPerformed,
}: {
  currency: string;
  reportCalculatorInputs: any;
  revenueProjection: number;
  editOpen: boolean;
  setEditOpen: React.Dispatch<React.SetStateAction<boolean>>;
  reportId: string;
  reportType: ReportTypes;
  handleReport: any;
  reportKPIData: any;
  trackEditPerformed: (editType: any) => void;
}) => {
  const theme = useTheme();

  const { monthly_rent, monthly_expense, one_time_cost } = reportCalculatorInputs;
  const monthlyProfit = reportKPIData.monthly_profit;

  const graphObj = [3, 6, 12, 24].map(nMonths => {
    const nMonthsTotalExpense = Math.round(monthly_expense * nMonths + one_time_cost);
    const nMonthsTotalRev = Math.round((revenueProjection / 12) * nMonths);
    return {
      nMonths,
      nMonthsTotalExpense,
      nMonthsTotalRev,
    };
  });

  const [mutableCalculatorInputs, setMutableCalculatorInputs] = useState({
    monthly_rent,
    monthly_expense,
    one_time_cost,
  });

  const immutableCalculatorData = {
    monthly_rent,
    monthly_expense,
    one_time_cost,
    monthlyProfit,
  };

  const handleValueChange = (key: string, event: any) => {
    let value = event.target.value;
    if (!value) {
      value = '0';
    }
    value = parseFloat(value.replaceAll(',', ''));
    setMutableCalculatorInputs({
      ...mutableCalculatorInputs,
      [key]: value,
    });
  };

  const handleOnRentCalcApply = () => {
    trackEditPerformed('calculator');
    const rentCalculatorMutableFieldsJsonObj = {
      update_type: 'calculator',
      id: reportId,
      report_type: reportType,
      ...mutableCalculatorInputs,
    };
    handleReport('UPDATE', rentCalculatorMutableFieldsJsonObj);
  };

  const RentCalculatorEditModal = () => {
    return (
      <Modal open={editOpen} onClose={() => setEditOpen(false)}>
        <Box sx={styles.filterModalBodyRoot}>
          <IconButton
            onClick={() => {
              setEditOpen(false);
            }}
            sx={styles.filterModalCloseIcon}
          >
            <CloseIcon />
          </IconButton>

          <Grid container xs justifyContent="space-between" sx={{ mt: '10px' }}>
            <Grid
              item
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
              sx={{ mt: 2 }}
            >
              <Grid item container spacing={2}>
                {rentalArbitrageCalculatorInputFields.map((field, subfieldIndex) => {
                  return (
                    <Grid item xs={6} key={subfieldIndex}>
                      <InputTextField
                        field={field}
                        stateObj={mutableCalculatorInputs}
                        handleValueChange={handleValueChange}
                        currency={currency}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>

            <Grid container item xs justifyContent="flex-end" sx={{ mt: '20px' }}>
              <Button onClick={handleOnRentCalcApply} sx={styles.actionSectionButton}>
                Apply
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    );
  };

  const immutableRentCalculatorFields = [
    ...rentalArbitrageCalculatorInputFields,
    {
      key: 'monthlyProfit',
      label: 'Monthly Profit',
      tooltip: '',
      unit: '$',
    },
  ];

  const CustomTooltip = ({ active, payload, label, classes }) => {
    if (active && payload) {
      return (
        <Card>
          <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              {payload[0].payload.nMonths} months Total Expense: {exchangeRates[currency].symbol}
              {formatCurrency(payload[0].payload.nMonthsTotalExpense.toString())}
              <br />
              {payload[0].payload.nMonths} months Total Revenue: {exchangeRates[currency].symbol}
              {formatCurrency(payload[0].payload.nMonthsTotalRev.toString())}
              <Divider style={{ background: DARK_GREY, marginTop: 10, marginBottom: 10 }} />
              {payload[0].payload.nMonths} months Total Profit: {exchangeRates[currency].symbol}
              {formatCurrency(
                (
                  payload[0].payload.nMonthsTotalRev - payload[0].payload.nMonthsTotalExpense
                ).toString()
              )}
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return null;
  };

  const combineFormatterYAxis = (val: number) => {
    return `${currency && exchangeRates[currency].symbol}` + abbreviateNumber(val);
  };

  return (
    <>
      <Grid
        item
        container
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        columnSpacing={4}
        rowSpacing={4}
      >
        {Object.keys(immutableRentCalculatorFields).map((field: string) => {
          return (
            <Grid
              item
              key={field}
              xs={6}
              sx={{
                [theme.breakpoints.down('md')]: {
                  marginRight: 10,
                },
              }}
            >
              <TooltipNew
                title={immutableRentCalculatorFields[field].label}
                content={immutableRentCalculatorFields[field].tooltip}
                arrow
              >
                {ImmutableDataField(
                  immutableRentCalculatorFields[field],
                  immutableCalculatorData[immutableRentCalculatorFields[field].key],
                  currency
                )}
              </TooltipNew>
            </Grid>
          );
        })}
      </Grid>

      <Grid item xs={12} sx={{ mt: 2 }}>
        <ResponsiveContainer height={300} width="100%">
          <BarChart
            data={graphObj}
            margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="nMonths"
              style={{
                fontSize: '0.65rem',
                fontFamily: 'Roboto',
              }}
            />
            <YAxis
              style={{
                fontSize: '0.65rem',
                fontFamily: 'Roboto',
              }}
              tickFormatter={combineFormatterYAxis}
            />
            <Tooltip
              contentStyle={{
                fontSize: '0.85rem',
                fontFamily: 'Roboto',
              }}
              cursor={{ fill: 'transparent' }}
              content={<CustomTooltip classes active payload label />}
            />
            <Bar
              type="monotone"
              dataKey="nMonthsTotalExpense"
              fill="#e69c5a"
              stackId="a"
              unit={exchangeRates[currency].symbol}
              barSize={20}
              background={{ fill: '	#FFFFFF' }}
            />
            <Bar
              type="monotone"
              dataKey="nMonthsTotalRev"
              fill="#4d9cd6"
              unit={exchangeRates[currency].symbol}
              barSize={20}
              stackId="b"
              background={{ fill: '	#FFFFFF' }}
            />
          </BarChart>
        </ResponsiveContainer>
      </Grid>
      {RentCalculatorEditModal()}
    </>
  );
};

const ManageCalculator = ({
  currency,
  reportCalculatorInputs,
  revenueProjection,
  editOpen,
  setEditOpen,
  reportId,
  reportType,
  handleReport,
  trackEditPerformed,
  reportKPIData,
}: {
  currency: string;
  reportCalculatorInputs: any;
  revenueProjection: number;
  editOpen: boolean;
  setEditOpen: React.Dispatch<React.SetStateAction<boolean>>;
  reportId: string;
  reportType: ReportTypes;
  handleReport: any;
  trackEditPerformed: (editType: any) => void;
  reportKPIData: any;
}) => {
  const theme = useTheme();

  const {
    annual_operating_expenses: {
      management_fee,
      property_tax,
      lodging_tax,
      hoa_fee_annual,
      maintenance_annual,
      insurance_annual,
      electricity_monthly,
      wifi_monthly,
      gas_monthly,
      others_annual,
    },
    annual_operating_expenses_total,
    owner_occupied_days,
  } = reportCalculatorInputs;

  const immutableCalculatorData = {
    annual_revenue_ltm: revenueProjection,
    annual_operating_expenses_total: reportKPIData.yearly_expense,
    owner_occupied_days,
  };

  const [mutableCalculatorInputs, setMutableCalculatorInputs] = useState({
    management_fee,
    property_tax,
    lodging_tax,
    hoa_fee_annual,
    maintenance_annual,
    insurance_annual,
    electricity_monthly,
    wifi_monthly,
    gas_monthly,
    others_annual,
    owner_occupied_days,
  });

  const handleValueChange = (key: string, event: any) => {
    let value = event.target.value;
    if (!value) {
      value = '0';
    }

    value = parseFloat(value.replaceAll(',', ''));

    setMutableCalculatorInputs({
      ...mutableCalculatorInputs,
      [key]: value,
    });
  };

  const handleOwnerOccupiedDaysChange = (event: any, newValue: number | number[]) => {
    setMutableCalculatorInputs({
      ...mutableCalculatorInputs,
      owner_occupied_days: newValue,
    });
  };

  const handleOnManageCalcApply = () => {
    trackEditPerformed('calculator');
    const manageCalculatorMutableFieldsJsonObj = {
      update_type: 'calculator',
      id: reportId,
      report_type: reportType,
      ...mutableCalculatorInputs,
    };
    handleReport('UPDATE', manageCalculatorMutableFieldsJsonObj);
  };

  const ManageCalculatorEditModal = () => {
    return (
      <Modal open={editOpen} onClose={() => setEditOpen(false)}>
        <Box sx={styles.filterModalBodyRoot}>
          <IconButton
            onClick={() => {
              setEditOpen(false);
            }}
            sx={styles.filterModalCloseIcon}
          >
            <CloseIcon />
          </IconButton>

          <Grid container xs justifyContent="space-between" sx={{ mt: '10px' }}>
            {/* Operating expenses setion */}
            <Grid
              item
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
              sx={{ mt: 2 }}
            >
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontSize: '16px' }}>
                  Operating Expenses
                </Typography>
              </Grid>

              <Grid item container spacing={2}>
                {managementCalInputKeys.annualExpenses.subfields &&
                  managementCalInputKeys.annualExpenses.subfields.map((subfield, subfieldIndex) => {
                    return (
                      <Grid item xs={6} key={subfieldIndex}>
                        <InputTextField
                          field={subfield}
                          stateObj={mutableCalculatorInputs}
                          handleValueChange={handleValueChange}
                          currency={currency}
                        />
                      </Grid>
                    );
                  })}
              </Grid>
            </Grid>

            <Grid
              item
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
              sx={{ mt: 2 }}
            >
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontSize: '16px' }}>
                  Number of owner's occupied days
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Slider
                  size="small"
                  value={mutableCalculatorInputs.owner_occupied_days}
                  min={0}
                  max={365}
                  step={1}
                  marks={[
                    {
                      value: 0,
                      label: '0',
                    },
                    {
                      value: 365,
                      label: '365',
                    },
                  ]}
                  draggable={true}
                  onChange={handleOwnerOccupiedDaysChange}
                  valueLabelDisplay="auto"
                  sx={{
                    color: COLOR_PRIMARY_1,
                    width: '100%',
                  }}
                />
              </Grid>
            </Grid>

            <Grid container item xs justifyContent="flex-end" sx={{ mt: '20px' }}>
              <Button onClick={handleOnManageCalcApply} sx={styles.actionSectionButton}>
                Apply
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    );
  };

  return (
    <>
      <Grid
        item
        container
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        columnSpacing={4}
        rowSpacing={4}
      >
        {Object.keys(managementCalInputKeys).map((field: string) => {
          return (
            <Grid
              item
              key={field}
              xs={6}
              sx={{
                [theme.breakpoints.down('md')]: {
                  marginRight: 10,
                },
              }}
            >
              <TooltipNew
                title={managementCalInputKeys[field].label}
                content={managementCalInputKeys[field].tooltip}
                arrow
              >
                {ImmutableDataField(
                  managementCalInputKeys[field],
                  immutableCalculatorData[managementCalInputKeys[field].key],
                  currency
                )}
              </TooltipNew>
            </Grid>
          );
        })}
      </Grid>
      {ManageCalculatorEditModal()}
    </>
  );
};

const ReportPageCalculatorSection = ({
  currency,
  reportType,
  reportId,
  reportCalculatorInputs,
  revenueProjection,
  handleReport,
  reportKPIData,
  isUserReportOwner,
  trackEditOpened,
  trackEditPerformed,
  isOnboardingComplete,
  isUserSubscribed,
  handleLoginOrPricingModalOpen,
}: {
  currency: string;
  reportType: ReportTypes;
  reportId: string;
  reportCalculatorInputs: any;
  revenueProjection: number;
  handleReport: any;
  reportKPIData: any;
  isUserReportOwner: boolean;
  trackEditOpened: (editType: any) => void;
  trackEditPerformed: (editType: any) => void;
  isOnboardingComplete: boolean;
  isUserSubscribed: boolean;
  handleLoginOrPricingModalOpen: (state: 'open' | 'close', forceOpen?: 'payment' | null) => void;
}) => {
  const [editOpen, setEditOpen] = useState<boolean>(false);
  const CalculatorMap = {
    buy: (
      <BuyCalculator
        currency={currency}
        reportCalculatorInputs={reportCalculatorInputs}
        revenueProjection={revenueProjection}
        editOpen={editOpen}
        setEditOpen={setEditOpen}
        reportId={reportId}
        reportType={reportType}
        handleReport={handleReport}
        trackEditPerformed={trackEditPerformed}
        reportKPIData={reportKPIData}
      />
    ),
    rent: (
      <RentCalculator
        currency={currency}
        reportCalculatorInputs={reportCalculatorInputs}
        revenueProjection={revenueProjection}
        editOpen={editOpen}
        setEditOpen={setEditOpen}
        reportId={reportId}
        reportType={reportType}
        handleReport={handleReport}
        reportKPIData={reportKPIData}
        trackEditPerformed={trackEditPerformed}
      />
    ),
    manage: (
      <ManageCalculator
        currency={currency}
        reportCalculatorInputs={reportCalculatorInputs}
        revenueProjection={revenueProjection}
        editOpen={editOpen}
        setEditOpen={setEditOpen}
        reportId={reportId}
        reportType={reportType}
        handleReport={handleReport}
        trackEditPerformed={trackEditPerformed}
        reportKPIData={reportKPIData}
      />
    ),
  };

  return (
    <Grid>
      <Grid
        container
        sx={{ ...styles.paper, position: 'relative' }}
        justifyContent="flex-start"
        alignItems="flex-start"
      >
        <Grid container item justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">Forecast your Income</Typography>
          <EditButton
            isDisabled={!isUserReportOwner}
            actionOnClick={() => {
              setEditOpen(true);
              trackEditOpened('calculator');
            }}
            isLocked={false}
          />
        </Grid>
        {CalculatorMap[reportType]}
      </Grid>
    </Grid>
  );
};

export default ReportPageCalculatorSection;
